import { HubTileLayoutKind } from "client/Api/Hub/common";
import {
	ReactElement,
	useCallback,
	useContext
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { AnnouncementsLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/announcementProvider";
import { KnowledgeItemLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/knowledgeItemProvider";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { EventLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/eventProvider";
import { DisplayHubTileLayout } from "client/Api/Workspace/workspace";
import { CategoryView } from "./CategoryView";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { SpotlightView } from "./SpotlightView";
import { FeedView } from "./FeedView";
import { CommunityLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/communityProvider";
import { useCreateHubItemDialog } from "client/Features/Hub/CreateHubItemDialog/CreateHubItemDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type HubLiveTileContentProps = {
	layout: DisplayHubTileLayout;
	hideHeader: boolean;
	hideFooter: boolean;
	announcementProvider: AnnouncementsLiveInformationProvider;
	knowledgeItemProvider: KnowledgeItemLiveInformationProvider;
	eventProvider: EventLiveInformationProvider;
	communityProvider: CommunityLiveInformationProvider;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const HubLiveTileContent = (props: HubLiveTileContentProps): ReactElement => {
	const {
		layout,
		hideHeader,
		hideFooter,
		announcementProvider,
		knowledgeItemProvider,
		eventProvider,
		communityProvider,
		onOpenTileMenu
	} = props;

	const {
		id,
		name,
		dimension,
		icon,
		environmentAppId,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const userTimezone: TimeZone = useUserTimezone();
	const [createHubItemDialog, openCreateHubItemDialog] = useCreateHubItemDialog();

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const createNewItemHandler = useCallback(
		(itemKind: HubItemKind, preferredCategoryId?: number | null): void => {
			addAnalyticsEvent();

			openCreateHubItemDialog({
				kind: itemKind,
				preferredCategoryId
			});
		},
		[addAnalyticsEvent, openCreateHubItemDialog]
	);

	const renderLayout = (): ReactElement => {
		switch (layout.kind) {
			case HubTileLayoutKind.List:
			case HubTileLayoutKind.Carousel:
			case HubTileLayoutKind.Grid:
				return (
					<CategoryView
						tileId={id}
						tileName={name}
						dimension={dimension}
						icon={icon}
						environmentAppId={environmentAppId}
						layout={layout}
						hideHeader={hideHeader}
						hideFooter={hideFooter}
						announcementProvider={announcementProvider}
						knowledgeItemProvider={knowledgeItemProvider}
						eventProvider={eventProvider}
						onAddAnalyticsEvent={addAnalyticsEvent}
						timezone={userTimezone}
						onCreateNewItem={createNewItemHandler}
					/>
				);
			case HubTileLayoutKind.Spotlight:
				return (
					<SpotlightView
						icon={icon}
						dimension={dimension}
						layout={layout}
						hideHeader={hideHeader}
						hideFooter={hideFooter}
						announcementProvider={announcementProvider}
						knowledgeItemProvider={knowledgeItemProvider}
						eventProvider={eventProvider}
						onAddAnalyticsEvent={addAnalyticsEvent}
						timezone={userTimezone}
					/>
				);
			case HubTileLayoutKind.Feed:
				return (
					<FeedView
						icon={icon}
						tileId={id}
						tileName={name}
						hideHeader={hideHeader}
						hideFooter={hideFooter}
						onAddAnalyticsEvent={addAnalyticsEvent}
						communityProvider={communityProvider}
						dimension={dimension}
					/>
				);
			default:
				never(layout);
		}
	};

	return (
		<>
			<LiveTileContent
				icon={icon}
				name={name}
				overlay={overlayState}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
			>
				{renderLayout()}
			</LiveTileContent>

			{createHubItemDialog}
		</>
	);
};
HubLiveTileContent.displayName = "HubLiveTileContent";
